import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

interface DocumentationSection {
  id: string;
  title: string;
  icon: string;
  description: string;
  topics: DocumentationTopic[];
}

interface DocumentationTopic {
  question: string;
  answer: string;
  steps?: string[];
  tips?: string[];
  images?: string[]; // Array of image paths
}

@Component({
  selector: 'app-documentation',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './documentation.component.html',
  styleUrls: ['./documentation.component.css'],
})
export class DocumentationComponent {
  activeSection: string = 'getting-started';
  searchQuery: string = '';
  selectedImage: string = '';

  constructor(private router: Router) {}

  goBack(): void {
    this.router.navigate(['/settings']);
  }

  openImageModal(imagePath: string): void {
    this.selectedImage = imagePath;
    const modalEl = document.getElementById('imageModal');
    if (modalEl) {
      const modal = new (window as any).bootstrap.Modal(modalEl);
      modal.show();
    }
  }

  documentationSections: DocumentationSection[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: 'bi-rocket',
      description: 'Learn the basics of using the app',
      topics: [
        {
          question: 'How do I add this app to my phone\'s home screen?',
          answer: 'Adding the app to your home screen gives you quick access like a native app. Follow the steps below for your device.',
          steps: [
            'iPhone/iPad: Open the app in Safari, tap the Share button (square with arrow), scroll down and tap "Add to Home Screen", then tap "Add"',
            'Android: Open the app in Chrome, tap the three-dot menu, tap "Add to Home Screen" or "Install App", then tap "Add" or "Install"',
          ],
          images: [
            'assets/home-screen/hs-1.jpeg',
            'assets/home-screen/hs-2.jpeg',
            'assets/home-screen/hs-3.jpeg',
            'assets/home-screen/hs-4.jpeg',
          ],
          tips: [
            'The app will appear on your home screen with its own icon',
            'Once added, you can open it just like any other app',
            'You\'ll get a full-screen experience without browser bars',
            'The app works offline for viewing saved schedules',
          ],
        },
        {
          question: 'How do I log in?',
          answer: 'You can log in using either your PIN or email/password.',
          steps: [
            'Go to the login page',
            'Enter your name/email',
            'Enter your 4-6 digit PIN or password',
            'Click "Sign In"',
          ],
          tips: [
            'PIN login is faster for quick access',
            'Use email/password for full account recovery options',
          ],
        },
        {
          question: 'How do I set up my account?',
          answer:
            'Complete your profile setup to enable all features and notifications.',
          steps: [
            'Go to Settings from the navigation menu',
            'Click on "Personal Information" to add your email',
            'Click on "Security Settings" to set up PIN and password',
            'Save your changes',
          ],
          tips: [
            'Setting up email enables schedule notifications',
            'A password allows account recovery if you forget your PIN',
          ],
        },
        {
          question: 'How do I navigate the app?',
          answer: 'Use the navigation menu to access different features.',
          steps: [
            'Click the menu icon (☰) in the top-left corner',
            'Select the feature you want to use',
            'Use the back button or menu to navigate between pages',
          ],
        },
      ],
    },
    {
      id: 'schedules',
      title: 'Viewing Schedules',
      icon: 'bi-calendar-week',
      description: 'Access and understand your work schedule',
      topics: [
        {
          question: 'How do I view my schedule?',
          answer:
            'Your published schedules are available on the main schedule page.',
          steps: [
            'Go to "Published Schedules" from the menu',
            'Click on any week to view the full schedule',
            'Your shifts are highlighted for easy viewing',
          ],
          tips: [
            'Use "View Today" for quick access to today\'s shift',
            'Use "View Tomorrow" to plan ahead',
          ],
        },
        {
          question: 'What do the schedule colors mean?',
          answer: 'Colors help you quickly identify your work status.',
          steps: [
            'Green = You have a shift scheduled',
            'Gray = You are off that day',
            'Yellow/Orange = Requested time off',
          ],
        },
        {
          question: 'How can I see who else is working?',
          answer: 'View your coworkers\' schedules in the full week view.',
          steps: [
            'Open any published schedule',
            'Click "View Today" or "View Tomorrow"',
            'Scroll down to see "Also Working Today/Tomorrow" section',
          ],
        },
      ],
    },
    {
      id: 'time-off',
      title: 'Requesting Time Off',
      icon: 'bi-calendar-x',
      description: 'Submit and track time-off requests',
      topics: [
        {
          question: 'How do I request time off?',
          answer: 'Submit time-off requests through the Requests page.',
          steps: [
            'Go to "Requests" from the menu',
            'Click "Request Time Off"',
            'Select the date you need off',
            'Add a reason (optional)',
            'Click "Submit Request"',
          ],
          tips: [
            'Submit requests as early as possible',
            'You can track request status on the Requests page',
            'Pending requests show in yellow, approved in green',
          ],
        },
        {
          question: 'Can I cancel a time-off request?',
          answer:
            'Contact your manager to cancel pending or approved requests.',
          steps: [
            'Go to Settings',
            'Click on Support',
            'Submit a ticket requesting cancellation',
            'Include the date you want to cancel',
          ],
        },
        {
          question: 'How will I know if my request is approved?',
          answer: 'Check the status on your Requests page.',
          steps: [
            'Go to "Requests" from the menu',
            'Look for the status badge next to each request',
            'Pending = Yellow, Approved = Green, Declined = Red',
          ],
        },
      ],
    },
    {
      id: 'shift-swaps',
      title: 'Shift Swaps',
      icon: 'bi-arrow-left-right',
      description: 'Exchange shifts with coworkers',
      topics: [
        {
          question: 'How do I request a shift swap?',
          answer: 'You can swap any shift (including trading a working shift for a day off, or vice versa). The swap requires approval from the other employee and a manager.',
          steps: [
            'Go to "Shift Swap" from the menu',
            'Click on the shift you want to give up (can be a working shift or a day off)',
            'Browse available shifts from all employees and filter by day or employee name',
            'Select the shift you want to take (can also be a working shift or day off)',
            'Review the swap details and add optional notes',
            'Submit the request',
          ],
          tips: [
            'You can swap a working shift for someone\'s day off',
            'You can swap your day off for someone\'s working shift',
            'Use filters to find specific days or employees',
            'Only future shifts are available for swapping',
          ],
        },
        {
          question: 'How does the shift swap approval process work?',
          answer:
            'Shift swaps require two approvals: first from the other employee, then from a manager. Once both approve, the schedule is automatically updated.',
          steps: [
            'You submit the swap request',
            'The other employee receives the request under "Pending Your Approval"',
            'If the employee approves, it moves to "Pending Manager Approval"',
            'A manager reviews and either approves or declines',
            'If approved, the published schedule is automatically updated with the new shifts',
            'Both employees can see the updated schedule immediately',
          ],
          tips: [
            'You\'ll see a notification badge when someone requests to swap with you',
            'Managers will see all pending swaps that need their approval',
            'Either the employee or manager can decline with a reason',
            'Approved swaps update the schedule automatically - no manual changes needed',
          ],
        },
        {
          question: 'Can I see pending shift swap requests?',
          answer: 'Yes, there are three tabs to manage all your swap requests.',
          steps: [
            'Go to "Shift Swap" from the menu',
            '"New Request" tab: Create a new swap request',
            '"My Requests" tab: View all swaps you\'ve requested and their status',
            '"Pending Your Approval" tab: See swaps where you need to approve (either as employee or manager)',
          ],
          tips: [
            'Status badges show: Pending Employee (yellow), Pending Manager (blue), Approved (green), Declined (red)',
            'You can see who declined a request and their reason',
            'Notification badges show how many requests need your attention',
          ],
        },
        {
          question: 'What types of shifts can I swap?',
          answer: 'You can swap any combination of working shifts and days off.',
          steps: [
            'Working shift for working shift: Trade your 9-5 Tuesday for someone\'s 2-10 Wednesday',
            'Working shift for day off: Give up your Friday shift to take someone\'s Saturday off',
            'Day off for working shift: Trade your Monday off to take someone\'s Monday shift',
            'Day off for day off: Trade your Thursday off for someone\'s Sunday off',
          ],
          tips: [
            'Green "Day Off" badges help identify off days',
            'All shifts show the date and day of the week',
            'Filters help you find specific types of shifts you want',
          ],
        },
      ],
    },
    {
      id: 'settings',
      title: 'Account Settings',
      icon: 'bi-gear',
      description: 'Manage your profile and preferences',
      topics: [
        {
          question: 'How do I update my email?',
          answer: 'Update your email in the Personal Information section.',
          steps: [
            'Go to Settings',
            'Click "Personal Information"',
            'Enter your new email address',
            'Click "Save Changes"',
          ],
          tips: [
            'Use a valid email you check regularly',
            'This email will be used for notifications',
          ],
        },
        {
          question: 'How do I change my PIN?',
          answer: 'Update your PIN in the Security Settings section.',
          steps: [
            'Go to Settings',
            'Click "Security Settings"',
            'Enter a new 4-6 digit PIN',
            'Click "Save Changes"',
          ],
          tips: [
            'Choose a PIN you\'ll remember',
            'Don\'t share your PIN with others',
          ],
        },
        {
          question: 'I forgot my PIN. What should I do?',
          answer:
            'If you set up a password, you can log in with email/password instead.',
          steps: [
            'Go to the login page',
            'Enter your email address',
            'Enter your password',
            'After logging in, go to Settings to reset your PIN',
          ],
          tips: [
            'This is why setting up a password is important!',
            'If you don\'t have a password, contact your manager',
          ],
        },
      ],
    },
    {
      id: 'support',
      title: 'Getting Help',
      icon: 'bi-headset',
      description: 'Contact support and submit tickets',
      topics: [
        {
          question: 'How do I get help or report an issue?',
          answer: 'Use the Support ticket system to get assistance from an admin.',
          steps: [
            'Go to Settings',
            'Click on "Support"',
            'Select where the issue occurred from the dropdown (Schedule, Requests, Shift Swap, Settings, or Other)',
            'Enter a brief description of your issue',
            'Optionally attach a screenshot to help explain the problem',
            'Click "Submit Ticket"',
          ],
          tips: [
            'Be specific about the issue',
            'Include any error messages you see',
            'Screenshots help admins understand the problem faster',
            'An admin will review and address your ticket',
          ],
        },
        {
          question: 'What happens after I submit a ticket?',
          answer: 'Your ticket is sent to the admin team who will review and address it.',
          steps: [
            'You\'ll see a "Ticket Submitted" confirmation message',
            'The admin team will review your ticket',
            'They will work to resolve your issue or respond with questions',
            'You may be contacted via email if you have one set up',
          ],
          tips: [
            'Ticket submission is currently uncommon, so response times are quick',
            'Make sure your email is set up in Personal Information for updates',
            'For urgent issues, you can also contact your manager directly',
          ],
        },
        {
          question: 'What types of issues can I report?',
          answer: 'You can report any issue or ask questions about the app.',
          steps: [
            'Schedule issues: Problems viewing or understanding your schedule',
            'Request issues: Time-off requests not working or questions about status',
            'Shift Swap issues: Problems with swap requests or approvals',
            'Settings issues: Trouble updating email, PIN, or password',
            'Other: Any other questions, suggestions, or technical problems',
          ],
          tips: [
            'Include which section of the app the issue is in',
            'Screenshots are very helpful for technical issues',
            'You can also use tickets to ask questions or make suggestions',
          ],
        },
      ],
    },
  ];

  setActiveSection(sectionId: string) {
    this.activeSection = sectionId;
  }

  get filteredSections(): DocumentationSection[] {
    if (!this.searchQuery.trim()) {
      return this.documentationSections;
    }

    const query = this.searchQuery.toLowerCase();
    return this.documentationSections
      .map((section) => ({
        ...section,
        topics: section.topics.filter(
          (topic) =>
            topic.question.toLowerCase().includes(query) ||
            topic.answer.toLowerCase().includes(query)
        ),
      }))
      .filter((section) => section.topics.length > 0);
  }

  get currentSection(): DocumentationSection | undefined {
    return this.documentationSections.find((s) => s.id === this.activeSection);
  }

  onSearch(event: any) {
    this.searchQuery = event.target.value;
  }

  clearSearch() {
    this.searchQuery = '';
  }
}